import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    IDepositRemainderResponse,
    IWithdrawalRemainderResponse,
} from "@finbackoffice/clientbff-client";
import { ClientBFFContext } from "@finbackoffice/site-core";
import { WalletsContext, ConfigContextV2 } from "contexts";

export const useDepositWithdrawalCountLimitsV2 = () => {
    const client = useContext(ClientBFFContext);
    const { currentCurrency } = useContext(WalletsContext);
    const { siteDefaultCurrency } = useContext(ConfigContextV2);
    const depositCountInterval = useRef<NodeJS.Timeout>(undefined);
    const withdrawalCountInterval = useRef<NodeJS.Timeout>(undefined);
    const [nextUpdateDepositCount, setNextUpdateDepositCount] = useState<number>(0);
    const [nextUpdateWithdrawalCount, setNextUpdateWithdrawalCount] = useState<number>(0);
    const [depositRemainderResponse, setDepositRemainderResponse] =
        useState<IDepositRemainderResponse>();
    const [withdrawalRemainderResponse, setWithdrawalRemainderResponse] =
        useState<IWithdrawalRemainderResponse>();

    const loadDepositCountLimits = useCallback(async () => {
        const response = await client.get24HoursDepositRemainder(
            currentCurrency ?? siteDefaultCurrency,
        );
        setNextUpdateDepositCount(Number(response.next_update_count || 0));
        setDepositRemainderResponse(response);
        if (Number(response.remaining_count) <= 0 && response.next_update_count) {
            let current = Date.now();
            const interval = setInterval(() => {
                const currentDif = Date.now() - current;
                current = Date.now();
                setNextUpdateDepositCount((count) => count - currentDif);
            }, 1000);
            depositCountInterval.current = interval;
        }
    }, [client, currentCurrency, siteDefaultCurrency]);

    const loadWithdrawalCountLimits = useCallback(async () => {
        const response = await client.get24HoursWithdrawalRemainder(
            currentCurrency ?? siteDefaultCurrency,
        );
        setNextUpdateWithdrawalCount(Number(response.next_update_count || 0));
        setWithdrawalRemainderResponse(response);
        if (Number(response.remaining_count) <= 0 && response.next_update_count) {
            let current = Date.now();
            const interval = setInterval(() => {
                const currentDif = Date.now() - current;
                current = Date.now();
                setNextUpdateWithdrawalCount((count) => count - currentDif);
            }, 1000);
            withdrawalCountInterval.current = interval;
        }
    }, [client, currentCurrency, siteDefaultCurrency]);

    useEffect(() => {
        void loadDepositCountLimits();
        void loadWithdrawalCountLimits();

        return () => {
            if (depositCountInterval.current) {
                clearInterval(depositCountInterval.current);
            }
            if (withdrawalCountInterval.current) {
                clearInterval(withdrawalCountInterval.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return useMemo(
        () => ({
            nextUpdateDepositCount,
            nextUpdateWithdrawalCount,
            depositRemainderResponse,
            withdrawalRemainderResponse,
        }),
        [
            nextUpdateDepositCount,
            nextUpdateWithdrawalCount,
            depositRemainderResponse,
            withdrawalRemainderResponse,
        ],
    );
};
